import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IMAGES } from '../../assets/marketing/manifest';
import { HEALTH_CHECK_COPY } from '../content/healthCheck';
import { focal } from '../content/images';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import useFocusTrap from '../hooks/useFocusTrap';
import useInert from '../hooks/useInert';
import useReducedMotion from '../hooks/useReducedMotion';
import Button from '../primitives/Button';
import Picture from '../primitives/Picture';
import HealthCheckCapture from './HealthCheckCapture';
import { useHealthCheck } from './HealthCheckProvider';
import HealthCheckQuestions from './HealthCheckQuestions';
import HealthCheckSummary from './HealthCheckSummary';

const SLIDE_MS = 480; // --mk-dur

/**
 * The right-hand slide-in from the mockups (guidelines p.55), portaled to
 * <body> so it sits above everything.
 *
 * z-index ladder: nav 40 · mobile menu 50 · ConsentBanner 50 · backdrop 60 ·
 * this panel 61.
 *
 * Accessibility: role=dialog + aria-modal, focus trapped inside with the close
 * button focused first, Escape and the backdrop close, the app root is made
 * inert behind it, body scroll is locked, and focus returns to the control
 * that opened it. Under reduced motion it fades instead of sliding.
 *
 * Privacy: the questionnaire answers are read from the provider and never
 * leave this component tree.
 */
export default function HealthCheckPanel() {
  const { isOpen, close, state, dispatch } = useHealthCheck();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(isOpen);
  const [shown, setShown] = useState(false);

  // Mount, then flip data-open on the next frame so the transition runs;
  // on close, let the transition finish before unmounting.
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => setShown(true)) : (setShown(true), 0);
      return () => {
        if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      };
    }
    setShown(false);
    const timer = setTimeout(() => setMounted(false), reduced ? 0 : SLIDE_MS);
    return () => clearTimeout(timer);
  }, [isOpen, reduced]);

  useFocusTrap(panelRef, isOpen && mounted, { initialFocus: closeRef, onEscape: close });
  useInert(isOpen);
  useBodyScrollLock(isOpen);

  if (!mounted || typeof document === 'undefined') return null;


  return createPortal(
    <>
      <div
        className="mk-hc-backdrop"
        data-open={shown ? 'true' : 'false'}
        data-testid="hc-backdrop"
        onClick={close}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className="mk-root mk-hc-panel"
        data-open={shown ? 'true' : 'false'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hc-title"
      >
        <div className="mk-hc-panel__bar">
          <span aria-hidden="true" />
          <p id="hc-title" className="t-details" style={{ margin: 0 }}>
            {HEALTH_CHECK_COPY.panelTitle}
          </p>
          <button ref={closeRef} type="button" className="mk-hc-panel__close" onClick={close} aria-label="Close health check">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M2 2l14 14M16 2L2 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="mk-hc-panel__body">
          {state.phase === 'intro' && (
            <>
              <Picture
                image={IMAGES['portrait-swim']}
                alt=""
                sizes="300px"
                focal={focal('portrait-swim')}
                className="mk-hc-panel__portrait"
              />
              <h2 className="mk-hc-panel__question">{HEALTH_CHECK_COPY.introTitle}</h2>
              <p className="mk-hc-panel__hint t-body">{HEALTH_CHECK_COPY.introBody}</p>
              <Button variant="fill" onClick={() => dispatch({ type: 'START' })}>
                {HEALTH_CHECK_COPY.introCta}
              </Button>
            </>
          )}
          {state.phase === 'questions' && <HealthCheckQuestions state={state} dispatch={dispatch} />}
          {state.phase === 'capture' && <HealthCheckCapture dispatch={dispatch} />}
          {state.phase === 'done' && <HealthCheckSummary answers={state.answers} entryRef={state.entryRef} onClose={close} />}
        </div>
      </div>
    </>,
    document.body,
  );
}
